import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import {
	AtlBrowserStorageLocal,
	AtlBrowserStorageSession,
} from '@atlassian/browser-storage-controls/web-storage-service';
import { fg } from '@atlassian/jira-feature-gating';
import { STOREJS_KEY_PREFIX } from './constants.tsx';
import {
	type SetItemResult,
	type WebStorageProvider,
	type WebStorageProviderNew,
	WebStorageType,
} from './types.tsx';

import { isStorageAPIAvailable } from './isStorageAPIAvailable.tsx';

// Reliability is reported via batched operational analytics rather than a per-call backend log
// POST, which was competing with critical-path requests and regressing VC90. Sentry is skipped to
// keep this off the critical path. The key is included in the payload only when the caller passes
// it (write operations), so it can aid debugging without being reported for every operation.
const logStorageError = (operation: string, error: unknown, key?: string): void => {
	if (fg('jfp-vulcan-browser-storage-error-logs')) {
		fireErrorAnalytics({
			meta: {
				id: 'storageOperationError',
				packageName: 'jiraBrowserStorageProviders',
				teamName: 'jfp-vulcan',
			},
			attributes: key !== undefined ? { operation, key } : { operation },
			error: error instanceof Error ? error : new Error(`Failed to ${operation}`),
			skipSentry: true,
		});
	} else {
		// oxlint-disable-next-line no-console
		console.error(
			key !== undefined ? `Failed to ${operation} (key: ${key})` : `Failed to ${operation}`,
			error,
		);
	}
};

export const createStorageProvider = (
	storageType: (typeof WebStorageType)[keyof typeof WebStorageType],
	appPrefix: string | undefined,
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	store: any,
): WebStorageProvider | WebStorageProviderNew => {
	const normalizedAppPrefix = appPrefix ?? '';
	const namespacedStore = store.namespace(normalizedAppPrefix);
	// Uses the same key format as store.js: namespace ? '__storejs_' + namespace + '_' + key : key
	const getKeyWithPrefix = (key: string) =>
		normalizedAppPrefix.trim().length ? `${STOREJS_KEY_PREFIX}${normalizedAppPrefix}_${key}` : key;

	const storageAPI =
		storageType === WebStorageType.LocalStorage ? AtlBrowserStorageLocal : AtlBrowserStorageSession;

	const remove = (key: string) => {
		try {
			if (!isStorageAPIAvailable()) {
				return;
			}
			const prefixedKey = getKeyWithPrefix(key);
			storageAPI.removeItem(prefixedKey); // remove data from new store
			namespacedStore.remove(key); // remove data from old store as well
		} catch (err) {
			logStorageError('remove item', err);
		}
	};

	return {
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		set: (key: string, value: any) => {
			try {
				// Matches store.js behavior: set(key, undefined) calls remove(key)
				if (value === undefined) {
					return remove(key);
				}
				if (!isStorageAPIAvailable()) {
					return;
				}
				const prefixedKey = getKeyWithPrefix(key);
				storageAPI.setItem(prefixedKey, JSON.stringify(value)).then((setItemResult) => {
					if (setItemResult?.status === 'BLOCKED') {
						storageAPI.removeItem(prefixedKey);
					}
				});
			} catch (err) {
				logStorageError('set item', err, key);
			}
		},
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		setAsync: async (key: string, value: any): Promise<void> => {
			try {
				if (value === undefined) {
					return remove(key);
				}
				if (!isStorageAPIAvailable()) {
					return;
				}
				const prefixedKey = getKeyWithPrefix(key);
				const setItemResult: SetItemResult = await storageAPI.setItem(
					prefixedKey,
					JSON.stringify(value),
				);
				if (setItemResult !== undefined) {
					if (setItemResult.status === 'BLOCKED') {
						storageAPI.removeItem(prefixedKey);
						throw new Error('write was blocked by storage or consent preferences.');
					}
					if (setItemResult.status !== 'SUCCESS') {
						throw new Error('write failed');
					}
				}
			} catch (err) {
				logStorageError('set item', err, key);
				throw err;
			}
		},

		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		setStrictlyNecessaryItem: (key: string, value: any) => {
			try {
				// Matches store.js behavior: set(key, undefined) calls remove(key)
				if (value === undefined) {
					return remove(key);
				}
				if (!isStorageAPIAvailable()) {
					return;
				}
				const prefixedKey = getKeyWithPrefix(key);
				storageAPI.setStrictlyNecessaryItem(prefixedKey, JSON.stringify(value));
			} catch (err) {
				logStorageError('set strictly necessary item', err, key);
			}
		},
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		get: (key: string): any | undefined => {
			try {
				if (!isStorageAPIAvailable()) {
					return undefined;
				}
				const prefixedKey = getKeyWithPrefix(key);
				const item = storageAPI.getItem(prefixedKey);
				if (typeof item === 'string') {
					return JSON.parse(item);
				}
			} catch (err) {
				logStorageError('get item', err);
			}
		},
		remove,
		// Clears the complete storage
		clearAll: () => {
			try {
				if (!isStorageAPIAvailable()) {
					return;
				}
				storageAPI.clear();
				// clean up old keys too
				namespacedStore.clearAll();
			} catch (err) {
				logStorageError('clear storage', err);
			}
		},
		length: (): number | undefined => {
			try {
				if (!isStorageAPIAvailable()) {
					return 0;
				}
				return storageAPI.length();
			} catch (err) {
				logStorageError('get storage length', err);
				return 0;
			}
		},
		key: (index: number): string | null => {
			try {
				if (!isStorageAPIAvailable()) {
					return null;
				}
				return storageAPI.key(index);
			} catch (err) {
				logStorageError('get key at index', err);
				return null;
			}
		},
	};
};
